import { useState, type FormEvent } from 'react';
import { SITE_EMAIL, SITE_NAME } from '@/mocks/site';
import { submitReaddyForm } from '@/utils/formSubmit';

const ENDPOINT = 'https://readdy.ai/api/form/dah85vboh653ivfvnntg';

const TOPICS = ['赛程时间咨询', '报名与投稿', '投票与评审', '结果与奖励', '其他问题'];

export default function ContactSection() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    setStatus('loading');
    setMessage('');

    const result = await submitReaddyForm(
      form,
      ENDPOINT,
      'contact_alt',
      '已收到你的留言，我们会在两个工作日内回复。',
    );

    if (result.ok) {
      setStatus('success');
      setMessage(result.message);
      form.reset();
    } else {
      setStatus('error');
      setMessage(result.message);
    }
  };

  const inputClass =
    'w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400';

  return (
    <section id="contact" className="w-full px-4 md:px-6 py-16 md:py-24 bg-background-100" aria-labelledby="contact-heading">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <h2 id="contact-heading" className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">
            联系方式
          </h2>
          <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
            关于{SITE_NAME}赛程、活动安排或比赛进度的疑问，都可以通过下面的表单留言，也可以直接发邮件给我们。
          </p>

          <div className="mt-8 flex flex-col gap-4">
            <div className="flex items-start gap-3">
              <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-background-50 border border-background-200 text-primary-600">
                <i className="ri-mail-line text-lg"></i>
              </span>
              <div>
                <p className="text-xs text-foreground-500">邮箱</p>
                <a href={`mailto:${SITE_EMAIL}`} className="text-sm text-foreground-950 hover:text-primary-600">
                  {SITE_EMAIL}
                </a>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-background-50 border border-background-200 text-primary-600">
                <i className="ri-time-line text-lg"></i>
              </span>
              <div>
                <p className="text-xs text-foreground-500">回复时间</p>
                <p className="text-sm text-foreground-950">工作日 10:00 – 18:00，两个工作日内回复</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-background-50 border border-background-200 text-primary-600">
                <i className="ri-question-answer-line text-lg"></i>
              </span>
              <div>
                <p className="text-xs text-foreground-500">小提示</p>
                <p className="text-sm text-foreground-950">先看常见问题页，多数赛程疑问已有现成答案</p>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            id="about-contact-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8 flex flex-col gap-4"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-2">
                  称呼
                </label>
                <input id="contact-name" type="text" name="name" required maxLength={40} placeholder="怎么称呼你" className={inputClass} />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-2">
                  邮箱
                </label>
                <input id="contact-email" type="email" name="email" required maxLength={120} placeholder="用于接收回复" className={inputClass} />
              </div>
            </div>

            <div>
              <label htmlFor="contact-topic" className="block text-sm text-foreground-700 mb-2">
                咨询类别
              </label>
              <select id="contact-topic" name="topic" required defaultValue="" className={`${inputClass} cursor-pointer`}>
                <option value="" disabled>
                  请选择咨询类别
                </option>
                {TOPICS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-2">
                留言内容
              </label>
              <textarea
                id="contact-message"
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder="请描述你的问题，最多 500 字"
                className={`${inputClass} resize-none`}
              ></textarea>
            </div>

            <input
              type="text"
              name="contact_alt"
              className="form-guard-field"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <button
                type="submit"
                disabled={status === 'loading'}
                className="px-7 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60"
              >
                {status === 'loading' ? '提交中…' : '提交留言'}
              </button>
              <p className="text-xs text-foreground-500">提交后我们会在两个工作日内通过邮箱回复。</p>
            </div>

            {status === 'success' && (
              <p className="flex items-center gap-2 text-sm text-secondary-700">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-checkbox-circle-fill"></i>
                </span>
                {message}
              </p>
            )}
            {status === 'error' && (
              <p className="flex items-center gap-2 text-sm text-primary-700">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-error-warning-fill"></i>
                </span>
                {message}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}